import React, { useState, useEffect, useCallback, useRef } from "react"
import { DataGrid } from "@mui/x-data-grid"
import Alert from "@mui/material/Alert"
import Box from "@mui/material/Box"

// ---------------------------------------------------------------------------
// Row projection
// ---------------------------------------------------------------------------

const SELECT_COLUMNS = "id, first_name, last_name, address_1, city, country"

function projectRow(p) {
    return {
        id: p.id,
        name: `${p.first_name} ${p.last_name}`,
        address: p.address_1 ?? "—",
        city: p.city && p.country ? `${p.city} (${p.country})` : p.city ?? "—",
    }
}

const COLUMNS = [
    { field: "name",    headerName: "Nom",     flex: 2 },
    { field: "address", headerName: "Adresse", flex: 3 },
    { field: "city",    headerName: "Ville",   flex: 2 },
]

// ---------------------------------------------------------------------------
// PeopleTable
// ---------------------------------------------------------------------------

export default function PeopleTable({ supabase, selectedPersonId, setSelectedPersonId }) {
    const [rows, setRows]       = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError]     = useState(null)

    // A ref-based counter lets us cancel in-flight fetches when either the
    // component unmounts or a newer fetch supersedes the current one.
    // Rationale: realtime events can fire in quick succession (e.g. a bulk
    // import). By incrementing a counter and checking it after the await, only
    // the most recent fetch's result is applied, preventing stale overwrites.
    const fetchIdRef = useRef(0)

    // -------------------------------------------------------------------------
    // Single fetch function — reused by both initial load and realtime events.
    // -------------------------------------------------------------------------

    const fetchPeople = useCallback(
        async ({ initialLoad = false } = {}) => {
            const fetchId = ++fetchIdRef.current
            if (initialLoad) setLoading(true)

            const { data, error: fetchError } = await supabase
                .from("person")
                .select(SELECT_COLUMNS)
                .order("last_name", { ascending: true })

            if (fetchId !== fetchIdRef.current) return // superseded

            if (fetchError) {
                console.error("PeopleTable: fetch error", fetchError)
                setError(fetchError.message)
                setLoading(false)
                return
            }

            setRows((data ?? []).map(projectRow))
            setError(null)
            setLoading(false)
        },
        [supabase]
    )

    // Initial load — invalidate on unmount via the fetch-ID mechanism.
    useEffect(() => {
        fetchPeople({ initialLoad: true })
        return () => { fetchIdRef.current++ }
    }, [fetchPeople])

    // -------------------------------------------------------------------------
    // Realtime subscription
    // -------------------------------------------------------------------------

    useEffect(() => {
        const channel = supabase
            .channel("people_table_realtime")
            .on(
                "postgres_changes",
                { event: "*", schema: "public", table: "person" },
                () => fetchPeople()
            )
            .subscribe()

        return () => { supabase.removeChannel(channel) }
    }, [supabase, fetchPeople])

    // -------------------------------------------------------------------------
    // Render
    // -------------------------------------------------------------------------

    return (
        <Box>
            {error && (
                <Alert
                    severity="error"
                    onClose={() => setError(null)}
                    sx={{ mb: 1 }}
                >
                    Impossible de charger l'annuaire : {error}
                </Alert>
            )}
            <DataGrid
                autoHeight
                loading={loading}
                checkboxSelection
                disableMultipleRowSelection
                rows={rows}
                columns={COLUMNS}
                rowSelectionModel={selectedPersonId ? [selectedPersonId] : []}
                onRowSelectionModelChange={(model) => {
                    setSelectedPersonId(model.length ? model[0] : null)
                }}
                getRowClassName={(params) =>
                    params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
                }
                initialState={{
                    pagination: { paginationModel: { pageSize: 20 } },
                }}
                pageSizeOptions={[10, 20, 50]}
                density="compact"
            />
        </Box>
    )
}